import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Star, Trash2, Pencil, Check, X, ChevronDown, ChevronRight,
  Share2, CornerDownRight, ChevronUp, ChevronDown as ChevronDownIcon, Paperclip, Download, Calendar
} from 'lucide-react';
import authFetch from '../utils/authFetch';
import './TasksPage.css';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const todayIso = () => new Date().toISOString().split('T')[0];

const CREATOR_COLORS = [
  { bg: '#ede9fe', fg: '#6d28d9' },
  { bg: '#dbeafe', fg: '#1d4ed8' },
  { bg: '#dcfce7', fg: '#15803d' },
  { bg: '#fef3c7', fg: '#b45309' },
  { bg: '#fee2e2', fg: '#b91c1c' },
  { bg: '#e0f2fe', fg: '#0369a1' },
  { bg: '#fce7f3', fg: '#be185d' },
  { bg: '#ecfccb', fg: '#4d7c0f' }
];

const getCreatorColor = (userId) => {
  const numericId = Number(userId) || 0;
  return CREATOR_COLORS[numericId % CREATOR_COLORS.length];
};

const formatEuropeanDate = (value) => {
  if (!value) return '';
  const match = String(value).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return `${Number(match[3])}.${Number(match[2])}.${match[1]}`;
};

const TasksPage = () => {
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [permissionLevel, setPermissionLevel] = useState('edit');
  const [loadingLists, setLoadingLists] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [error, setError] = useState('');

  const [newListName, setNewListName] = useState('');
  const [newTaskForm, setNewTaskForm] = useState({ title: '', notes: '', dueDate: '' });
  const [newTaskExpanded, setNewTaskExpanded] = useState(false);
  const [newTaskShowDueDate, setNewTaskShowDueDate] = useState(false);
  const [newTaskFile, setNewTaskFile] = useState(null);
  const [addingTask, setAddingTask] = useState(false);
  const newTaskFileInputRef = useRef(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const [editingListId, setEditingListId] = useState(null);
  const [listNameDraft, setListNameDraft] = useState('');

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', notes: '', dueDate: '' });

  const [addingSubtaskFor, setAddingSubtaskFor] = useState(null);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const subtaskInputRef = useRef(null);
  const [expandedCompletedSubtasks, setExpandedCompletedSubtasks] = useState({});

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUsers, setShareUsers] = useState([]);
  const [shares, setShares] = useState([]);
  const [shareForm, setShareForm] = useState({ userId: '', permissionLevel: 'edit' });
  const [shareMessage, setShareMessage] = useState('');

  const [attachmentsByTask, setAttachmentsByTask] = useState({});
  const [expandedAttachmentsFor, setExpandedAttachmentsFor] = useState(null);
  const [uploadingAttachmentFor, setUploadingAttachmentFor] = useState(null);

  const selectedList = useMemo(() => lists.find((list) => list.id === selectedListId) || null, [lists, selectedListId]);
  const canEdit = permissionLevel === 'owner' || permissionLevel === 'edit';

  // Refreshes keep the current content on screen: swapping it for a loading
  // message shrinks the page and loses the scroll position.
  const loadLists = async (preferListId) => {
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load task lists');
      }
      const loadedLists = Array.isArray(payload.lists) ? payload.lists : [];
      setLists(loadedLists);

      if (loadedLists.length > 0) {
        const targetId = preferListId && loadedLists.some((list) => list.id === preferListId)
          ? preferListId
          : (selectedListId && loadedLists.some((list) => list.id === selectedListId) ? selectedListId : loadedLists[0].id);
        setSelectedListId(targetId);
      } else {
        setSelectedListId(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load task lists');
      setLists([]);
    } finally {
      setLoadingLists(false);
    }
  };

  const loadTasks = async (listId, { showLoading = false } = {}) => {
    if (!listId) {
      setTasks([]);
      return;
    }
    if (showLoading) setLoadingTasks(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${listId}/tasks`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load tasks');
      }
      setTasks(Array.isArray(payload.tasks) ? payload.tasks : []);
      setPermissionLevel(payload.permissionLevel || 'view');
    } catch (err) {
      setError(err.message || 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadShareUsers = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/users`);
      const payload = await response.json();
      if (response.ok) {
        setShareUsers(Array.isArray(payload.users) ? payload.users : []);
      }
    } catch (err) {
      // silent - share picker is a non-critical enhancement
    }
  };

  const loadShares = async (listId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${listId}/shares`);
      const payload = await response.json();
      if (response.ok) {
        setShares(Array.isArray(payload.shares) ? payload.shares : []);
      }
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    loadLists();
    authFetch(`${API_BASE}/api/tasks/notifications/seen`, { method: 'POST' }).catch(() => {});
  }, []);

  useEffect(() => {
    loadTasks(selectedListId, { showLoading: true });
  }, [selectedListId]);

  const handleCreateList = async (event) => {
    event.preventDefault();
    const name = newListName.trim();
    if (!name) return;
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to create list');
      }
      setNewListName('');
      await loadLists(payload.list?.id);
    } catch (err) {
      setError(err.message || 'Failed to create list');
    }
  };

  const startRenameList = (list) => {
    setEditingListId(list.id);
    setListNameDraft(list.name);
  };

  const saveRenameList = async (listId) => {
    const name = listNameDraft.trim();
    if (!name) return;
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${listId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to rename list');
      }
      setEditingListId(null);
      await loadLists(listId);
    } catch (err) {
      setError(err.message || 'Failed to rename list');
    }
  };

  const handleDeleteList = async (list) => {
    if (!window.confirm(`Delete the list "${list.name}" and all of its tasks? This cannot be undone.`)) {
      return;
    }
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${list.id}`, { method: 'DELETE' });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete list');
      }
      setSelectedListId(null);
      await loadLists();
    } catch (err) {
      setError(err.message || 'Failed to delete list');
    }
  };

  const handleMoveList = async (listId, direction) => {
    const index = lists.findIndex((list) => list.id === listId);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || targetIndex < 0 || targetIndex >= lists.length) return;

    const reordered = [...lists];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setLists(reordered);

    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listIds: reordered.map((list) => list.id) })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to reorder lists');
      }
    } catch (err) {
      setError(err.message || 'Failed to reorder lists');
      await loadLists(selectedListId);
    }
  };

  const openShareModal = async () => {
    if (!selectedList) return;
    setShareMessage('');
    setShareForm({ userId: '', permissionLevel: 'edit' });
    setShareModalOpen(true);
    await Promise.all([loadShareUsers(), loadShares(selectedList.id)]);
  };

  const handleAddShare = async (event) => {
    event.preventDefault();
    if (!shareForm.userId) return;
    setShareMessage('');
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${selectedList.id}/shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: Number(shareForm.userId), permissionLevel: shareForm.permissionLevel })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to share list');
      }
      setShareForm({ userId: '', permissionLevel: 'edit' });
      await loadShares(selectedList.id);
    } catch (err) {
      setShareMessage(err.message || 'Failed to share list');
    }
  };

  const handleRemoveShare = async (userId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${selectedList.id}/shares/${userId}`, { method: 'DELETE' });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to remove share');
      }
      await loadShares(selectedList.id);
    } catch (err) {
      setShareMessage(err.message || 'Failed to remove share');
    }
  };

  const resetNewTaskForm = () => {
    setNewTaskForm({ title: '', notes: '', dueDate: '' });
    setNewTaskFile(null);
    setNewTaskShowDueDate(false);
    setNewTaskExpanded(false);
  };

  const handleAddTask = async (event) => {
    event.preventDefault();
    const title = newTaskForm.title.trim();
    if (!title || !selectedListId) return;
    setAddingTask(true);
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${selectedListId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          notes: newTaskForm.notes.trim(),
          dueDate: newTaskForm.dueDate || ''
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to add task');
      }
      if (newTaskFile && payload.task?.id) {
        await handleUploadAttachment(payload.task.id, newTaskFile);
      }
      resetNewTaskForm();
      await loadTasks(selectedListId);
      await loadLists(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to add task');
    } finally {
      setAddingTask(false);
    }
  };

  const handleAddSubtask = async (parentTaskId) => {
    const title = subtaskTitle.trim();
    if (!title) return;
    // Clear before the request so anything typed while it runs isn't wiped afterwards.
    setSubtaskTitle('');
    subtaskInputRef.current?.focus();
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/lists/${selectedListId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, parentTaskId })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to add subtask');
      }
      await loadTasks(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to add subtask');
      setSubtaskTitle((current) => current || title);
    }
  };

  const cancelAddSubtask = () => {
    setAddingSubtaskFor(null);
    setSubtaskTitle('');
  };

  const patchTask = async (taskId, body) => {
    const response = await authFetch(`${API_BASE}/api/tasks/${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to update task');
    }
    return payload.task;
  };

  const handleToggleComplete = async (task) => {
    try {
      await patchTask(task.id, { completed: !task.completed });
      await loadTasks(selectedListId);
      await loadLists(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to update task');
    }
  };

  const handleToggleStar = async (task) => {
    try {
      await patchTask(task.id, { starred: !task.starred });
      await loadTasks(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to update task');
    }
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditForm({ title: task.title || '', notes: task.notes || '', dueDate: task.dueDate || '' });
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditForm({ title: '', notes: '', dueDate: '' });
  };

  const saveEditTask = async (taskId) => {
    if (!editForm.title.trim()) return;
    try {
      await patchTask(taskId, {
        title: editForm.title.trim(),
        notes: editForm.notes.trim(),
        dueDate: editForm.dueDate || ''
      });
      cancelEditTask();
      await loadTasks(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/${taskId}`, { method: 'DELETE' });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete task');
      }
      await loadTasks(selectedListId);
      await loadLists(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to delete task');
    }
  };

  const handleMoveTask = async (taskId, direction) => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/${taskId}/move`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to reorder task');
      }
      await loadTasks(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to reorder task');
    }
  };

  const loadAttachments = async (taskId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/${taskId}/attachments`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load attachments');
      }
      setAttachmentsByTask((prev) => ({ ...prev, [taskId]: Array.isArray(payload.attachments) ? payload.attachments : [] }));
    } catch (err) {
      setError(err.message || 'Failed to load attachments');
    }
  };

  const toggleAttachments = async (taskId) => {
    if (expandedAttachmentsFor === taskId) {
      setExpandedAttachmentsFor(null);
      return;
    }
    setExpandedAttachmentsFor(taskId);
    if (!attachmentsByTask[taskId]) {
      await loadAttachments(taskId);
    }
  };

  const handleUploadAttachment = async (taskId, file) => {
    if (!file) return;
    setUploadingAttachmentFor(taskId);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch(`${API_BASE}/api/tasks/${taskId}/attachments`, {
        method: 'POST',
        body: formData
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to upload attachment');
      }
      await loadAttachments(taskId);
      await loadTasks(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachmentFor(null);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/attachments/${attachment.id}/download`);
      if (!response.ok) {
        throw new Error('Failed to download attachment');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalName || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download attachment');
    }
  };

  const handleDeleteAttachment = async (taskId, attachmentId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/tasks/attachments/${attachmentId}`, { method: 'DELETE' });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to delete attachment');
      }
      await loadAttachments(taskId);
      await loadTasks(selectedListId);
    } catch (err) {
      setError(err.message || 'Failed to delete attachment');
    }
  };

  const topLevelTasks = tasks.filter((task) => !task.parentTaskId);
  const subtasksByParent = tasks.reduce((acc, task) => {
    if (task.parentTaskId) {
      if (!acc[task.parentTaskId]) acc[task.parentTaskId] = [];
      acc[task.parentTaskId].push(task);
    }
    return acc;
  }, {});

  const incompleteTopLevel = topLevelTasks.filter((task) => !task.completed);
  const completeTopLevel = topLevelTasks.filter((task) => task.completed);
  const isSharedList = !!selectedList && (!selectedList.isOwner || (selectedList.sharedWith && selectedList.sharedWith.length > 0));

  const renderTaskRow = (task, isSubtask = false) => {
    const isEditing = editingTaskId === task.id;
    const isOverdue = task.dueDate && !task.completed && task.dueDate < todayIso();
    const children = subtasksByParent[task.id] || [];
    const openChildren = children.filter((child) => !child.completed);
    const completedChildren = children.filter((child) => child.completed);
    const showCompletedChildren = !!expandedCompletedSubtasks[task.id];

    return (
      <div key={task.id} className={`task-item${isSubtask ? ' task-item-sub' : ''}${task.isNew ? ' task-item-new' : ''}`}>
        <div className="task-row">
          {isSubtask && <CornerDownRight size={14} className="task-sub-icon" />}
          <button
            className={`task-checkbox${task.completed ? ' task-checkbox-checked' : ''}`}
            onClick={() => handleToggleComplete(task)}
            disabled={!canEdit}
            title={task.completed ? 'Mark as not completed' : 'Mark as completed'}
          >
            {task.completed && <Check size={12} />}
          </button>

          {isEditing ? (
            <div className="task-edit-form">
              <input
                type="text"
                className="task-edit-input"
                value={editForm.title}
                onChange={(event) => setEditForm((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Task title"
                autoFocus
              />
              <input
                type="text"
                className="task-edit-input"
                value={editForm.notes}
                onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
                placeholder="Notes"
              />
              <input
                type="date"
                className="task-edit-input"
                value={editForm.dueDate}
                onChange={(event) => setEditForm((prev) => ({ ...prev, dueDate: event.target.value }))}
              />
              <div className="task-row-actions">
                <button className="task-icon-btn task-icon-btn-save" onClick={() => saveEditTask(task.id)} title="Save">
                  <Check size={14} />
                </button>
                <button className="task-icon-btn task-icon-btn-cancel" onClick={cancelEditTask} title="Cancel">
                  <X size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="task-body">
              <div className={`task-title${task.completed ? ' task-title-completed' : ''}`}>
                {task.title}
                {task.isNew && <span className="task-new-badge">New</span>}
              </div>
              {(task.notes || task.dueDate || (isSharedList && task.createdByDisplayName)) && (
                <div className="task-meta">
                  {task.dueDate && <span className={`task-due${isOverdue ? ' task-due-overdue' : ''}`}>{formatEuropeanDate(task.dueDate)}</span>}
                  {isSharedList && task.createdByDisplayName && (
                    <span
                      className="task-creator"
                      style={{
                        backgroundColor: getCreatorColor(task.createdByUserId).bg,
                        color: getCreatorColor(task.createdByUserId).fg
                      }}
                    >
                      Added by {task.createdByDisplayName}
                    </span>
                  )}
                  {task.notes && <span className="task-notes">{task.notes}</span>}
                </div>
              )}
            </div>
          )}

          {!isEditing && (
            <button
              className={`task-icon-btn${task.attachmentCount ? ' task-icon-btn-active' : ''}`}
              onClick={() => toggleAttachments(task.id)}
              title="Attachments"
            >
              <Paperclip size={14} />
              {task.attachmentCount > 0 && <span className="task-attachment-count">{task.attachmentCount}</span>}
            </button>
          )}

          {!isEditing && canEdit && (
            <div className="task-row-actions">
              <button className="task-icon-btn" onClick={() => handleMoveTask(task.id, 'up')} title="Move up">
                <ChevronUp size={14} />
              </button>
              <button className="task-icon-btn" onClick={() => handleMoveTask(task.id, 'down')} title="Move down">
                <ChevronDownIcon size={14} />
              </button>
              <button
                className={`task-icon-btn${task.starred ? ' task-icon-btn-starred' : ''}`}
                onClick={() => handleToggleStar(task)}
                title={task.starred ? 'Unstar' : 'Star'}
              >
                <Star size={14} fill={task.starred ? '#f59e0b' : 'none'} />
              </button>
              <button className="task-icon-btn" onClick={() => startEditTask(task)} title="Edit">
                <Pencil size={14} />
              </button>
              <button className="task-icon-btn task-icon-btn-danger" onClick={() => handleDeleteTask(task.id)} title="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>

        {expandedAttachmentsFor === task.id && (
          <div className="task-attachments-panel">
            {(attachmentsByTask[task.id] || []).length === 0 ? (
              <div className="tasks-empty">No files attached yet.</div>
            ) : (
              (attachmentsByTask[task.id] || []).map((attachment) => (
                <div key={attachment.id} className="task-attachment-row">
                  <span className="task-attachment-name">{attachment.originalName}</span>
                  {attachment.uploadedByDisplayName && <span className="task-attachment-by">by {attachment.uploadedByDisplayName}</span>}
                  <button className="task-icon-btn" onClick={() => handleDownloadAttachment(attachment)} title="Download">
                    <Download size={14} />
                  </button>
                  {canEdit && (
                    <button className="task-icon-btn task-icon-btn-danger" onClick={() => handleDeleteAttachment(task.id, attachment.id)} title="Remove">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
            {canEdit && (
              <label className="task-attachment-upload-btn">
                <Plus size={12} /> {uploadingAttachmentFor === task.id ? 'Uploading...' : 'Attach a file'}
                <input
                  type="file"
                  style={{ display: 'none' }}
                  disabled={uploadingAttachmentFor === task.id}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    handleUploadAttachment(task.id, file);
                    event.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        )}

        {children.length > 0 && (
          <div className="task-subtasks">
            {openChildren.map((child) => renderTaskRow(child, true))}
            {completedChildren.length > 0 && (
              <>
                <button
                  className="task-completed-subtasks-toggle"
                  onClick={() => setExpandedCompletedSubtasks((prev) => ({ ...prev, [task.id]: !prev[task.id] }))}
                  aria-expanded={showCompletedChildren}
                >
                  {showCompletedChildren ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Completed ({completedChildren.length})
                </button>
                {showCompletedChildren && completedChildren.map((child) => renderTaskRow(child, true))}
              </>
            )}
          </div>
        )}

        {!isSubtask && canEdit && (
          addingSubtaskFor === task.id ? (
            <div className="task-subtask-add-row">
              <CornerDownRight size={14} className="task-sub-icon" />
              <input
                type="text"
                className="task-edit-input"
                value={subtaskTitle}
                onChange={(event) => setSubtaskTitle(event.target.value)}
                placeholder="Subtask title"
                ref={subtaskInputRef}
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleAddSubtask(task.id);
                  if (event.key === 'Escape') cancelAddSubtask();
                }}
              />
              <button className="task-icon-btn task-icon-btn-save" onClick={() => handleAddSubtask(task.id)} title="Add"><Check size={14} /></button>
              <button className="task-icon-btn task-icon-btn-cancel" onClick={cancelAddSubtask} title="Cancel"><X size={14} /></button>
            </div>
          ) : (
            <button className="task-add-subtask-btn" onClick={() => { setAddingSubtaskFor(task.id); setSubtaskTitle(''); }}>
              <Plus size={12} /> Add subtask
            </button>
          )
        )}
      </div>
    );
  };

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <div>
          <h1 className="tasks-title">Tasks</h1>
          <p className="tasks-subtitle">Personal to-do lists with subtasks, due dates, and sharing.</p>
        </div>
      </div>

      {error && <div className="tasks-alert">{error}</div>}

      <div className="tasks-layout">
        <div className="tasks-sidebar">
          <form onSubmit={handleCreateList} className="tasks-new-list-form">
            <input
              type="text"
              value={newListName}
              onChange={(event) => setNewListName(event.target.value)}
              placeholder="New list name"
              className="tasks-input"
            />
            <button type="submit" className="task-icon-btn task-icon-btn-save" title="Create list">
              <Plus size={16} />
            </button>
          </form>

          {loadingLists ? (
            <div className="tasks-empty">Loading lists...</div>
          ) : lists.length === 0 ? (
            <div className="tasks-empty">No lists yet.</div>
          ) : (
            <div className="tasks-list-nav">
              {lists.map((list, listIndex) => (
                <div key={list.id} className={`tasks-list-item${list.id === selectedListId ? ' tasks-list-item-active' : ''}`}>
                  {editingListId === list.id ? (
                    <div className="tasks-list-rename-row">
                      <input
                        type="text"
                        className="task-edit-input"
                        value={listNameDraft}
                        onChange={(event) => setListNameDraft(event.target.value)}
                        autoFocus
                        onKeyDown={(event) => { if (event.key === 'Enter') saveRenameList(list.id); }}
                      />
                      <button className="task-icon-btn task-icon-btn-save" onClick={() => saveRenameList(list.id)}><Check size={14} /></button>
                      <button className="task-icon-btn task-icon-btn-cancel" onClick={() => setEditingListId(null)}><X size={14} /></button>
                    </div>
                  ) : (
                    <>
                      <div className="tasks-list-reorder">
                        <button
                          className="tasks-list-reorder-btn"
                          onClick={() => handleMoveList(list.id, 'up')}
                          disabled={listIndex === 0}
                          title="Move list up"
                          aria-label={`Move ${list.name} up`}
                        >
                          <ChevronUp size={12} />
                        </button>
                        <button
                          className="tasks-list-reorder-btn"
                          onClick={() => handleMoveList(list.id, 'down')}
                          disabled={listIndex === lists.length - 1}
                          title="Move list down"
                          aria-label={`Move ${list.name} down`}
                        >
                          <ChevronDownIcon size={12} />
                        </button>
                      </div>
                      <button className="tasks-list-select-btn" onClick={() => setSelectedListId(list.id)}>
                        {list.hasNewTasks && <span className="tasks-list-new-dot" title="New tasks" />}
                        <span className="tasks-list-name">{list.name}</span>
                        {list.openTaskCount > 0 && <span className="tasks-list-count">{list.openTaskCount}</span>}
                        {!list.isOwner && <span className="tasks-list-shared-badge">Shared</span>}
                      </button>
                      {list.isOwner && (
                        <div className="tasks-list-item-actions">
                          <button className="task-icon-btn" onClick={() => startRenameList(list)} title="Rename"><Pencil size={12} /></button>
                          <button className="task-icon-btn" onClick={() => handleDeleteList(list)} title="Delete"><Trash2 size={12} /></button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="tasks-main">
          {!selectedList ? (
            <div className="tasks-empty">Create a list to get started.</div>
          ) : (
            <>
              <div className="tasks-main-header">
                <div>
                  <div className="tasks-main-title">{selectedList.name}</div>
                  {!selectedList.isOwner && <div className="tasks-main-owner">Shared by {selectedList.ownerDisplayName || 'another user'} ({permissionLevel === 'edit' ? 'can edit' : 'view only'})</div>}
                  {selectedList.isOwner && selectedList.sharedWith && selectedList.sharedWith.length > 0 && (
                    <div className="tasks-main-owner">
                      Shared with {selectedList.sharedWith.map((share) => `${share.displayName || 'Unknown user'} (${share.permissionLevel === 'edit' ? 'can edit' : 'view only'})`).join(', ')}
                    </div>
                  )}
                </div>
                {selectedList.isOwner && (
                  <button className="tasks-share-btn" onClick={openShareModal}>
                    <Share2 size={16} /> Share
                  </button>
                )}
              </div>

              {canEdit && (
                <form onSubmit={handleAddTask} className="tasks-add-task-card">
                  <div className="tasks-add-task-row">
                    <span className="task-checkbox task-checkbox-placeholder" />
                    <div className="tasks-add-task-inputs">
                      <input
                        type="text"
                        value={newTaskForm.title}
                        onChange={(event) => setNewTaskForm((prev) => ({ ...prev, title: event.target.value }))}
                        onFocus={() => setNewTaskExpanded(true)}
                        placeholder="Add a task"
                        className="tasks-add-task-title-input"
                      />
                      {newTaskExpanded && (
                        <input
                          type="text"
                          value={newTaskForm.notes}
                          onChange={(event) => setNewTaskForm((prev) => ({ ...prev, notes: event.target.value }))}
                          placeholder="Details"
                          className="tasks-add-task-details-input"
                        />
                      )}
                    </div>
                  </div>

                  {newTaskExpanded && (
                    <div className="tasks-add-task-footer">
                      <div className="tasks-add-task-icons">
                        <button
                          type="button"
                          className={`task-icon-btn${newTaskForm.dueDate ? ' task-icon-btn-active' : ''}`}
                          onClick={() => setNewTaskShowDueDate((prev) => !prev)}
                          title="Add date"
                        >
                          <Calendar size={14} />
                        </button>
                        {newTaskShowDueDate && (
                          <input
                            type="date"
                            className="task-edit-input tasks-add-task-date-input"
                            value={newTaskForm.dueDate}
                            onChange={(event) => setNewTaskForm((prev) => ({ ...prev, dueDate: event.target.value }))}
                          />
                        )}
                        {!newTaskShowDueDate && newTaskForm.dueDate && (
                          <span className="task-due">{formatEuropeanDate(newTaskForm.dueDate)}</span>
                        )}

                        <button
                          type="button"
                          className={`task-icon-btn${newTaskFile ? ' task-icon-btn-active' : ''}`}
                          onClick={() => newTaskFileInputRef.current?.click()}
                          title="Attach file"
                        >
                          <Paperclip size={14} />
                        </button>
                        <input
                          type="file"
                          ref={newTaskFileInputRef}
                          style={{ display: 'none' }}
                          onChange={(event) => setNewTaskFile(event.target.files?.[0] || null)}
                        />
                        {newTaskFile && (
                          <span className="tasks-add-task-file-chip">
                            {newTaskFile.name}
                            <button type="button" className="tasks-add-task-file-remove" onClick={() => setNewTaskFile(null)}><X size={12} /></button>
                          </span>
                        )}
                      </div>

                      <div className="tasks-add-task-buttons">
                        <button type="button" className="tasks-add-task-cancel-btn" onClick={resetNewTaskForm}>Cancel</button>
                        <button type="submit" className="tasks-share-btn" disabled={!newTaskForm.title.trim() || addingTask}>
                          {addingTask ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              )}

              {loadingTasks ? (
                <div className="tasks-empty">Loading tasks...</div>
              ) : incompleteTopLevel.length === 0 && completeTopLevel.length === 0 ? (
                <div className="tasks-empty">No tasks yet. Add one above.</div>
              ) : (
                <>
                  <div className="tasks-list-body">
                    {incompleteTopLevel.map((task) => renderTaskRow(task))}
                  </div>

                  {completeTopLevel.length > 0 && (
                    <div className="tasks-completed-section">
                      <button className="tasks-completed-toggle" onClick={() => setShowCompleted((prev) => !prev)}>
                        {showCompleted ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        Completed ({completeTopLevel.length})
                      </button>
                      {showCompleted && (
                        <div className="tasks-list-body">
                          {completeTopLevel.map((task) => renderTaskRow(task))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {shareModalOpen && selectedList && (
        <div className="tasks-modal-overlay" onClick={() => setShareModalOpen(false)}>
          <div className="tasks-modal" onClick={(event) => event.stopPropagation()}>
            <div className="tasks-modal-header">
              <div className="tasks-modal-title">Share "{selectedList.name}"</div>
              <button className="task-icon-btn" onClick={() => setShareModalOpen(false)}><X size={16} /></button>
            </div>

            {shareMessage && <div className="tasks-alert">{shareMessage}</div>}

            <form onSubmit={handleAddShare} className="tasks-share-form">
              <select
                value={shareForm.userId}
                onChange={(event) => setShareForm((prev) => ({ ...prev, userId: event.target.value }))}
                className="tasks-input"
                required
              >
                <option value="">Select a user</option>
                {shareUsers
                  .filter((user) => !shares.some((share) => share.userId === user.id))
                  .map((user) => (
                    <option key={user.id} value={user.id}>{user.displayName}</option>
                  ))}
              </select>
              <select
                value={shareForm.permissionLevel}
                onChange={(event) => setShareForm((prev) => ({ ...prev, permissionLevel: event.target.value }))}
                className="tasks-input"
              >
                <option value="edit">Can edit</option>
                <option value="view">View only</option>
              </select>
              <button type="submit" className="tasks-share-btn">Share</button>
            </form>

            <div className="tasks-share-list">
              {shares.length === 0 ? (
                <div className="tasks-empty">Not shared with anyone yet.</div>
              ) : (
                shares.map((share) => (
                  <div key={share.id} className="tasks-share-row">
                    <span>{share.displayName}</span>
                    <span className="tasks-share-permission">{share.permissionLevel === 'edit' ? 'Can edit' : 'View only'}</span>
                    <button className="task-icon-btn task-icon-btn-danger" onClick={() => handleRemoveShare(share.userId)} title="Remove access">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
